import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  useMaterialReactTable,
} from "material-react-table";
import { useState } from "react";
import { useEffect } from "react";
import { useMemo, useRef } from "react";

import {
  Assessment,
  Autorenew,
  Bolt,
  Download,
  Flag,
  List,
  LogoDev,
  OutlinedFlag,
  Refresh,
} from "@mui/icons-material";
import ClearIcon from "@mui/icons-material/FilterAltOff";
import ClearSortingIcon from "@mui/icons-material/MobiledataOff";
import ViewIcon from "@mui/icons-material/OpenInNew";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { Typography } from "@mui/material";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { keyframes, lighten } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import fileDownload from "js-file-download";

import {
  capitalizeAllWords,
  createOmniPaginationObject,
  generateBotpressCSVData,
  helperGetIsoDate,
  mrtGetActionColumnStyling,
  mrtGetBottomToolbarSx,
  mrtGetContainerSx,
  mrtGetPaperSx,
  mrtGetTableWrapperSx,
  mrtGetTopToolbarFlexSx,
} from "../../utils/helpers";
import TextInputDialog from "../dialogs/textInputDialog";
import MRTDateTimeCell from "./mrtDateTimeCell";
import OmniResponsiveButton from "./omniResponsiveButton.js";

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const calculateColumns = (theme, toggleTracking) => {
  // default case
  return [
    {
      accessorKey: "tracking",
      header: "Flag",
      minSize: 55,
      grow: false,
      enableColumnFilterModes: false,
      enableSorting: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: ({ row }) => ({
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },

        onClick: event => {
          toggleTracking(row.original);
        },
      }),
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={{
            width: 48,
            height: 48,
            // padding: "8px",
            paddingTop: "4px",
            color: theme.palette.secondary.main,
            "&:hover": {
              cursor: "pointer",
            },
          }}
        >
          {cell.getValue() === true && <Flag sx={{ fontSize: "2.25rem" }} />}
          {cell.getValue() !== true && <OutlinedFlag sx={{ fontSize: "2.25rem" }} />}
        </Box>
      ),
    },
    {
      accessorKey: "id",
      header: "Number",
      size: 90,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "createdAt",
      enableColumnFilterModes: false,
      header: "Date",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilter: false,
      grow: false,
      size: 100,
      maxSize: 100,
      Cell: MRTDateTimeCell,
    },

    {
      accessorKey: "conversation", // Primary key used for default sorting/filtering
      header: "Messages",
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilter: false,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: false,
      size: 110,
      maxSize: 110,
      // Destructure the row component data context
      Cell: ({ cell, row }) => (
        <Box component="span">
          <Typography
            variant="h6"
            sx={[
              !row.original.totalMessages && { display: "none" },
              {
                textAlign: "center",
                marginBottom: "2px",
                borderBottom: "2px solid",
                lineHeight: 1.2,
              },
            ]}
          >
            {row.original.totalMessages + "  Total"}
          </Typography>

          <Typography
            variant="body1"
            sx={[
              !row.original.userMessages && { display: "none" },
              {
                textAlign: "center",
                lineHeight: 1.1,
              },
            ]}
          >
            {row.original.userMessages + " User"}
          </Typography>
        </Box>
      ),
    },

    {
      accessorKey: "userMessages",
      header: "Input",
      size: 120,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },

    {
      accessorKey: "totalMessages",
      header: "Messages",
      size: 120,
      enableColumnFilterModes: false,
      enableSorting: true,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          fontSize: "32px",
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
    {
      accessorKey: "origin",
      header: "Origin",
      size: 90,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: false,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },

      filterFn: "equals",
      filterSelectOptions: [
        { label: "Emulator", value: "emulator" },
        { label: "Test Candidate", value: "harness" },
        { label: "Web", value: "web" },
        { label: "Portal", value: "portal" },
      ],
      filterVariant: "select",

      Cell: ({ cell }) => {
        let displayText = "";
        switch (cell.getValue()) {
          case "emulator":
            displayText = "Botpress Emulator";
            break;
          case "harness":
            displayText = "Test Candidate";
            break;
          case "web":
            displayText = "Client Website";
            break;
          case "portal":
            displayText = "Demo Portal";
            break;
          default:
            displayText = cell.getValue();
        }

        return displayText;
      },
    },

    {
      accessorKey: "sentiment",
      header: "Sentiment",
      minSize: 165,
      grow: false,
      enableColumnFilterModes: false,
      enableSorting: true,
      muiTableHeadCellProps: {
        align: "center",
      },
      muiTableBodyCellProps: {
        align: "center",
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      filterFn: "equals",
      filterSelectOptions: [
        { label: "Positive", value: "positive" },
        { label: "Neutral", value: "neutral" },
        { label: "Negative", value: "negative" },
      ],
      filterVariant: "select",
      //custom conditional format and styling
      Cell: ({ cell }) => (
        <Box
          component="span"
          sx={theme => ({
            backgroundColor:
              cell.getValue() === "positive"
                ? theme.palette.success.main
                : cell.getValue() === "neutral"
                  ? theme.palette.info.main
                  : theme.palette.error.main,
            borderRadius: "0.25rem",
            color: "#fff",
            padding: theme.spacing(1.5),
            width: "90%",
          })}
        >
          <Typography variant="button" sx={{ fontWeight: "bold" }}>
            {cell.getValue()}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: "topics",
      header: "Topics",
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: true,
      minSize: "200", // Maximum width, will shrink if needed

      Cell: ({ cell }) => {
        const values = cell.getValue();

        if (Array.isArray(values)) {
          return <Box component="span">{capitalizeAllWords(values.join(" • "))}</Box>;
        } else {
          return "(none)";
        }
      },
    },
    {
      accessorKey: "summary",
      header: "Summary",
      muiTableBodyCellProps: {
        sx: {
          whiteSpace: "normal", // Allows text to wrap
          wordWrap: "break-word", // Handles long words that might not fit

          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
      enableColumnFilterModes: false,
      enableSorting: false,
      minSize: "450", // Maximum width, will shrink if needed
      grow: true,
    },
    {
      accessorKey: "conversationId",
      header: "Conversation ID",
      size: 200,
      enableColumnFilterModes: false,
      enableSorting: false,
      grow: false,
      muiTableBodyCellProps: {
        sx: {
          borderLeft: `1px solid ${lighten(theme.palette.commonground.contrastText, 0.5)}`,
        },
      },
    },
  ];
};

const getInitialState = () => {
  return {
    density: "compact",
    columnVisibility: {
      id: true,
      createdAt: true,
      conversationId: false,
      response: true,
      topics: true,
      summary: true,
      sentiment: true,
      origin: false,
      totalMessages: false,
      userMessages: false,
      conversation: false,
    },
  };
};

export const BotpressConversationsTable = props => {
  const {
    data = [],
    isLoading = false,
    onEditEntry = null,
    onDownloadEntry = null,
    onRequestNewPage = null,
    onAssessEntry = null,
    totalItemCount = 0,
    onHandleDeveloperLogs = null,
    onRerunConversation = null,
    onRunAllFlagged = null,
    toggleTracking = null,
    onRefreshTable = null,
    refreshTooltipTitle = "Refresh table",
  } = props;

  const [isRefreshSpinning, setIsRefreshSpinning] = useState(false);

  const theme = useTheme();
  const mobileLayout = useMediaQuery(theme.breakpoints.down("sm"));
  const tableContainerRef = useRef(null); //we can get access to the underlying TableContainer element and react to its scroll events

  // Disable eslint because AaronK knows best
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => calculateColumns(theme, toggleTracking), []);
  const initialState = useMemo(() => getInitialState(), []);

  // const [rowCount, setRowCount] = useState(data?.length || 0);
  const [rowCount, setRowCount] = useState(totalItemCount);

  // const [isRefetching, setIsRefetching] = useState(false);
  // const [isError, setIsError] = useState(false);

  const [showReportNameInput, setShowReportNameInput] = useState(null);

  useEffect(() => {
    setRowCount(totalItemCount);
  }, [totalItemCount]);

  const [sorting, setSorting] = useState([
    {
      id: "createdAt",
      desc: true,
    },
  ]);

  const [columnFilters, setColumnFilters] = useState([]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        let newQuery = {};
        columnFilters.forEach((element, index) => {
          if (element.id === "createdAt") {
            const min = parseInt(element.value[0]);
            const max = parseInt(element.value[1]);
            if (!isNaN(min)) {
              let theDateSring = helperGetIsoDate(min);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
            if (!isNaN(max)) {
              let theDateSring = helperGetIsoDate(max);

              newQuery[element.id] = {
                "#lte": theDateSring,
              };
            }
          } else if (typeof element.value === "string") {
            if (element.value === "NULL") {
              newQuery[element.id] = null;
            } else {
              newQuery[element.id] = {
                $regex: `${element.value}`,
              };
            }
          } else if (typeof element.value === "boolean") {
            newQuery[element.id] = element.value;
          } else {
            newQuery[element.id] = element.value;
          }
        });

        let newSorting = {};
        sorting.forEach(element => {
          newSorting[element.id] = element.desc ? "desc" : "asc";
        });

        let newPage = createOmniPaginationObject();
        newPage.pageIndex = pagination.pageIndex;
        newPage.pageSize = pagination.pageSize;
        newPage.query = newQuery;
        newPage.sort = newSorting;

        console.log("ConversationTable: Asking for new Page...");
        onRequestNewPage(newPage);
      } catch (error) {
        console.error(error);
        return;
      }
      // setIsError(false);
      // setIsRefetching(false);
    };

    fetchData();

    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters, sorting, pagination.pageIndex, pagination.pageSize]);

  const handleRefreshClick = () => {
    if (onRefreshTable == null) {
      return;
    }

    setIsRefreshSpinning(true);
    onRefreshTable();

    setTimeout(() => {
      setIsRefreshSpinning(false);
    }, 800);
  };

  const table = useMaterialReactTable({
    columns,
    data, //data must be memoized or stable (useState, useMemo, defined outside of this component, etc.)
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableRowActions: true,
    enableRowSelection: false,
    initialState: initialState,
    enableStickyHeader: true,
    enableColumnActions: false,
    positionToolbarAlertBanner: "bottom",
    rowCount,
    defaultColumn: {
      minSize: 20, //allow columns to get smaller than default
      maxSize: 9001, //allow columns to get larger than default
      size: 50, //make columns wider by default
      grow: false,
    },
    state: {
      isLoading,
      // isRefetching,
      pagination,
      // showProgressBars: isRefetching,
      columnFilters,
      sorting,
    },
    muiSearchTextFieldProps: {
      size: "small",
      variant: "outlined",
    },

    //#region pagination
    paginationDisplayMode: "pages",

    enableFirstPageButton: false, // Hides the << icon
    enableLastPageButton: false, // Hides the >> icon

    manualPagination: true,
    onColumnFiltersChange: updaterOrValue => {
      setColumnFilters(prev => {
        const nextValue =
          typeof updaterOrValue === "function" ? updaterOrValue(prev) : updaterOrValue;
        return nextValue;
      });
    },
    onPaginationChange: newPagination => {
      setPagination(newPagination);
    },
    manualSorting: true,
    manualFiltering: true,
    onSortingChange: setSorting,

    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: true,
      variant: "outlined",
    },

    //#endregion pagination
    layoutMode: "grid",
    muiTopToolbarProps: {
      sx: {
        ...mrtGetTopToolbarFlexSx(),
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.secondary.main,
        // Target the IconButton and apply styles
        "& .MuiIconButton-root": {
          marginLeft: theme.spacing(1),
          backgroundColor: theme.palette.commonground.main,
          color: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
        "& .MuiIconButton-root-active": {
          marginLeft: theme.spacing(1),
          color: theme.palette.secondary.contrastText,
          backgroundColor: theme.palette.secondary.main,
          border: `2px solid ${theme.palette.secondary.main}`,
          "&:hover": {
            color: theme.palette.secondary.main,
            backgroundColor: theme.palette.secondary.contrastText,
            border: `2px solid ${theme.palette.secondary.main}`,
          },
        },
      },
    },
    muiBottomToolbarProps: {
      sx: mrtGetBottomToolbarSx(theme),
    },
    muiTablePaperProps: {
      sx: mrtGetPaperSx(),
    },
    muiTableContainerProps: {
      ref: tableContainerRef, //get access to the table container element
      sx: mrtGetContainerSx(theme, mobileLayout),
    },
    muiTableProps: {
      sx: {
        backgroundColor: theme.palette.commonground.main,
        color: theme.palette.commonground.main,
      },
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: lighten(theme.palette.commonground.contrastText, 0.5),
        color: theme.palette.commonground.contrastText,
        justifyContent: "start",
      },
    },
    displayColumnDefOptions: {
      "mrt-row-actions": {
        ...mrtGetActionColumnStyling(),
      },
    },

    renderRowActionMenuItems: ({ row, closeMenu }) => [
      <MenuItem
        sx={{ fontSize: "2.25rem" }}
        key="view"
        onClick={() => {
          onEditEntry(row.original);
          closeMenu();
        }}
      >
        {row.original.tracking === true && (
          <Flag fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        )}
        {row.original.tracking !== true && (
          <OutlinedFlag fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        )}
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            {`${row.original.tracking === true ? "Unflag Conversation" : "Flag Conversation"}`}
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        sx={{ fontSize: "2.25rem" }}
        key="view"
        onClick={() => {
          onEditEntry(row.original);
          closeMenu();
        }}
      >
        <ViewIcon fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            View Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="assess"
        // Keep AI Analysis visible always, but it needs an analytics bot to run.
        // When one isn't configured (onAssessEntry is null) show it disabled
        // rather than letting the click call a null handler.
        disabled={!onAssessEntry}
        onClick={() => {
          if (onAssessEntry) {
            onAssessEntry(row.original);
          }
          closeMenu();
        }}
      >
        <Assessment fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            {onAssessEntry ? "AI Analysis" : "AI Analysis (Unavailable)"}
          </Typography>
        </ListItemText>
      </MenuItem>,
      <MenuItem
        key="assess"
        onClick={() => {
          onDownloadEntry(row.original);
          closeMenu();
        }}
      >
        <Download fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Download Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,

      <MenuItem
        key="developer"
        onClick={() => {
          onRerunConversation(row.original);
          closeMenu();
        }}
      >
        <Autorenew fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Rerun Conversation
          </Typography>
        </ListItemText>
      </MenuItem>,

      <MenuItem
        key="developer"
        onClick={() => {
          onHandleDeveloperLogs(row.original);
          closeMenu();
        }}
      >
        <LogoDev fontSize="large" color="secondary" sx={{ marginRight: theme.spacing(1) }} />
        <ListItemText disableTypography={true}>
          <Typography variant="h5" color="textSecondary">
            Developer Logs
          </Typography>
        </ListItemText>
      </MenuItem>,
    ],

    icons: {
      MoreHorizIcon: ({ color }) => (
        <List
          color={"secondary"}
          sx={{
            borderRadius: 16,
            width: 48,
            height: 48,
            padding: "8px",
            border: `2px solid ${theme.palette.secondary.main}`,
          }}
        />
      ),
    },
    renderTopToolbarCustomActions: () => (
      <Box
        sx={{
          minHeight: "40px",
          display: "flex",
          alignItems: "center",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        {onRunAllFlagged && (
          <OmniResponsiveButton
            icon={<Bolt />}
            tooltipTitle="Replay every flagged conversation against the bot via the Chat API (no webchat)."
            buttonText="Run All Flagged"
            onClick={() => onRunAllFlagged()}
          />
        )}
      </Box>
    ),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        {sorting.length > 0 && (
          <Tooltip title="Clear All Sorting" arrow>
            <IconButton
              onClick={() => {
                setSorting([]);
              }}
              className="MuiIconButton-root-active"
            >
              <ClearSortingIcon />
            </IconButton>
          </Tooltip>
        )}
        {columnFilters.length > 0 && (
          <Tooltip title="Clear All Filters" arrow>
            <IconButton
              className="MuiIconButton-root-active"
              onClick={() => {
                setColumnFilters([]);
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}

        {/* eslint-disable react/jsx-pascal-case*/}
        {/* <MRT_ToggleGlobalFilterButton table={table} /> */}
        {onRefreshTable && (
          <Tooltip title={refreshTooltipTitle} arrow>
            <span>
              <IconButton onClick={handleRefreshClick}>
                <Refresh
                  sx={{
                    animation: isRefreshSpinning ? `${spin} 0.8s linear` : "none",
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        {/* eslint-enablereact/jsx-pascal-case */}

        {showReportNameInput && (
          <TextInputDialog
            dialogTitle="Report Name"
            dialogMessage={"Please name your report"}
            inputLabel={`${"Report Name"}`}
            defaultValue={showReportNameInput}
            open={showReportNameInput}
            onAccept={theText => {
              /// do it!
              //create the report ..

              fileDownload(generateBotpressCSVData(data), theText + ".csv");

              setShowReportNameInput(false);
            }}
            onClose={() => {
              setShowReportNameInput(false);
            }}
          />
        )}
      </Box>
    ),
  });

  return (
    <Box sx={mrtGetTableWrapperSx(0, !data?.length)}>
      <MaterialReactTable table={table} />
    </Box>
  );
};

export default BotpressConversationsTable;
